import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactCompareImage from 'react-compare-image';
import { projectsApi } from '../services/api';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await projectsApi.getById(id);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">
            {currentLang === 'ar' ? 'المشروع غير موجود' : 'Project not found'}
          </h2>
          <Link to="/projects" className="text-gold hover:underline">
            {currentLang === 'ar' ? 'العودة إلى المشاريع' : 'Back to projects'}
          </Link>
        </div>
      </div>
    );
  }

  const images = project.images || [];

  return (
    <main data-testid="project-detail-page" className="pt-24 pb-20">
      <div className="container-custom">
        {/* Back Link */}
        <Link
          to="/projects"
          data-testid="back-to-projects"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors mb-8"
        >
          <BackArrow size={18} />
          <span>{currentLang === 'ar' ? 'العودة إلى المشاريع' : 'Back to projects'}</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Main Image */}
            <div className="aspect-[4/3] overflow-hidden mb-4">
              <img
                src={images[currentImageIndex] || 'https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg'}
                alt={currentLang === 'ar' ? project.title_ar : project.title_en}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-colors ${
                      index === currentImageIndex ? 'border-gold' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <span className="label-text mb-4 block">
              {t(`projects.categories.${project.category}`)}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {currentLang === 'ar' ? project.title_ar : project.title_en}
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              {currentLang === 'ar' ? project.description_ar : project.description_en}
            </p>

            <Link
              to="/contact"
              data-testid="project-cta"
              className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold hover:text-charcoal transition-colors duration-300"
            >
              {t('nav.getQuote')}
            </Link>
          </motion.div>
        </div>

        {/* Before/After if available */}
        {project.before_image && project.after_image && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-20"
          >
            <h2 className="text-2xl font-bold text-foreground mb-8">
              {t('beforeAfter.title')}
            </h2>
            <div className="max-w-4xl">
              <ReactCompareImage
                leftImage={project.before_image}
                rightImage={project.after_image}
                leftImageLabel={t('beforeAfter.before')}
                rightImageLabel={t('beforeAfter.after')}
                sliderLineColor="#ef7f1a"
                sliderLineWidth={3}
              />
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default ProjectDetailPage;
